import React, { useMemo, useEffect, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import useToggle from "react-use/lib/useToggle";

import * as yup from "yup";

import { pickBy } from "lodash";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "@apollo/client";
import { useRouteMatch, useHistory } from "react-router-dom";

import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";

import { useTranslation } from "react-i18next";

import Input from "../../../components/Input";

import { PropertyType } from "../../../config/const/property";
import {
  QUERY_PROPERTY_COMPANY_CONTACT,
  QUERY_COMPANIES,
} from "../../../config/graphql/query";
import {
  UPDATE_PROPERTY_COMPANY,
  DELETE_COMPANY,
} from "../../../config/graphql/mutation";

const CompanyContactForm = React.memo(() => {
  const [show, setShow] = useToggle(false);
  const history = useHistory();

  const { t } = useTranslation(["company", "general"]);

  const {
    //@ts-ignore
    params: { id },
  } = useRouteMatch();

  const schema = useMemo(
    () =>
      yup.object().shape({
        website: yup.string().url(t("company:contact.yup.website")),
        email: yup.string().email(t("company:contact.yup.email")),
        phone: yup.string(),
        fax: yup.string(),
      }),
    [t]
  );

  const [onUpdate] = useMutation(UPDATE_PROPERTY_COMPANY);
  const [onDelete] = useMutation(DELETE_COMPANY);

  const methods = useForm({
    resolver: yupResolver(schema),
    shouldFocusError: false,
  });

  const { data: propertyData } = useQuery(QUERY_PROPERTY_COMPANY_CONTACT, {
    skip: !id,
    variables: { id },
  });

  useEffect(() => {
    methods.reset(propertyData?.property);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyData]);

  const onRemoveCompany = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      history.replace(`/company`);
      return onDelete({ variables: { id } })
        .then(() => {
          toast.success(t("company:contact.toast.deleted"));
        })
        .catch((error) => {
          toast.error(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message
          );
        });
    },
    [history, onDelete, id, t]
  );

  const onBeforeRemove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();

      setShow(true);
    },
    [setShow]
  );

  const onSubmit = (values: ICompany) => {
    const input = {
      //@ts-ignore
      ...pickBy(values),
      id,
      type: PropertyType.Company,
    };

    return onUpdate({ variables: { input } })
      .then(() => {
        toast.success(t("company:contact.toast.updated"));
      })
      .catch((error) => {
        toast.error(
          error?.networkError?.result?.errors?.[0]?.message ?? error?.message
        );
      });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="col-lg-4">
            <div className="form-group">
              <label htmlFor="website">
                {t("company:contact.form.website")}
              </label>
              <Input name="website" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="email">{t("company:contact.form.email")}</label>
              <Input type="email" name="email" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="phone">{t("company:contact.form.phone")}</label>
              <Input name="phone" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="fax">{t("company:contact.form.fax")}</label>
              <Input name="fax" className="form-control" />
            </div>
          </div>
          <div className="col-lg-4"></div>
          <div className="col-12">
            <input type="submit" className="btn btn-primary" />
            {id && (
              <button onClick={onBeforeRemove} className="btn btn-danger ml-3">
                {t("common:delete")}
              </button>
            )}
          </div>
        </form>
      </FormProvider>
      <Modal show={show} onHide={setShow} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>{t("company:contact.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("company:contact.modal.body")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={setShow}>
            {t("common:cancel")}
          </Button>
          <Button variant="danger" onClick={onRemoveCompany}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default CompanyContactForm;
